/**
 * Centralna usługa API do komunikacji z backendowymi funkcjami Vercel
 * Zastępuje bezpośrednie wywołania Supabase zgodnie z nową architekturą
 */

import { useAuthStore } from '../stores/authStore';
import logger from '../utils/logger';

// Typy API
export interface CreateInvitationRequest {
  email: string;
  role: 'parent_b' | 'caregiver' | 'child_viewer';
  familyId: string;
}

export interface CreateInvitationResponse {
  success: boolean;
  invitationId: string;
  invitationUrl: string;
}

export interface AcceptInvitationRequest {
  token: string;
  userId?: string;
  _timestamp?: string; // Dodane pole do zapobiegania cachowaniu
}

export interface AcceptInvitationResponse {
  success: boolean;
  message?: string;
  familyId?: string;
  invitation?: {
    email: string;
    role: string;
    expires_at: string;
    family_id: string;
  };
  requiresAuth?: boolean;
}

export interface ApiError {
  error: string;
  details?: any;
}

export interface FamilyMember {
  member_id: string;
  user_id: string;
  role: string;
  joined_at: string;
  invited_at?: string;
  user_email: string;
}

export interface FamilyInvitation {
  id: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'rejected';
  created_at: string;
  expires_at: string;
  invited_by: string;
  token: string;
  accepted_at?: string;
}

/**
 * Bazowa klasa API z obsługą autoryzacji
 */
class ApiService {
  private readonly baseUrl: string;
  // Prosty cache i deduplikacja żądań dla planu rodziny
  private familyPlanCache: Map<string, { data: { success: boolean; planId: 'basic' | 'pro' | 'max'; status: string; periodEnd?: string | null; priceId?: string | null }; expireAt: number }>; 
  private inflightFamilyPlan: Map<string, Promise<{ success: boolean; planId: 'basic' | 'pro' | 'max'; status: string; periodEnd?: string | null; priceId?: string | null }>>;
  // Globalny mutex na odświeżanie tokena (by uniknąć wielu równoległych refreshów)
  private refreshInFlight: Promise<string | null> | null = null;

  constructor() {
    // W środowisku lokalnym użyj localhost, w produkcji - obecną domenę
    this.baseUrl = process.env.NODE_ENV === 'development' 
      ? 'http://localhost:3000/api'
      : `${window.location.origin}/api`;
    this.familyPlanCache = new Map();
    this.inflightFamilyPlan = new Map();
  }

  /**
   * Zapewnia świeży access token z pojedynczym odświeżeniem współdzielonym między wywołaniami
   */
  private async getFreshAccessToken(): Promise<string | null> {
    if (this.refreshInFlight) {
      try { return await this.refreshInFlight; } catch { return null; }
    }
    try {
      const p = (async () => {
        const { supabase } = await import('../lib/supabase');
        const { data, error } = await supabase.auth.refreshSession();
        if (error) return null;
        return data.session?.access_token ?? null;
      })();
      this.refreshInFlight = p;
      const token = await p;
      return token;
    } finally {
      this.refreshInFlight = null;
    }
  }

  /**
   * Wykonuje żądanie API z automatyczną autoryzacją
   */
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const { session } = useAuthStore.getState();
    
    if (!session?.access_token) {
      throw new Error('Brak tokenu autoryzacji. Zaloguj się ponownie.');
    }

    const url = `${this.baseUrl}${endpoint}`;
    
    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        ...options.headers,
      },
    };

    logger.debug(`🌐 API Call: ${options.method || 'GET'} ${url}`);

    const doFetch = async (): Promise<Response> => fetch(url, config)

    try {
      let response = await doFetch();
      if (response.status === 401 || response.status === 419) {
        // Pojedynczy współdzielony refresh
        const newToken = await this.getFreshAccessToken();
        if (newToken) {
          (config.headers as any).Authorization = `Bearer ${newToken}`;
          response = await doFetch();
        }
      }

      if (!response.ok) {
        // Twardy fallback: jeśli wciąż 401 -> wyloguj by nie wpaść w limbo
        if (response.status === 401) {
          try { await useAuthStore.getState().signOut(); } catch {}
        }
        let errorText = `HTTP ${response.status}: ${response.statusText}`
        try {
          const errorData = await response.json();
          errorText = errorData.error || errorData.details || errorText
        } catch {}
        throw new Error(errorText);
      }

      const data = await response.json();
      logger.debug(`✅ API Success:`, data);
      return data;
    } catch (error) {
      logger.error(`❌ API Error for ${url}:`, error);
      throw error;
    }
  }

  /**
   * Tworzy nowe zaproszenie do rodziny
   */
  async createInvitation(request: CreateInvitationRequest): Promise<CreateInvitationResponse> {
    return this.request<CreateInvitationResponse>('/invitations/create', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Pobiera listę zaproszeń dla rodziny
   */
  async getInvitations(familyId: string): Promise<{ success: boolean; invitations: FamilyInvitation[]; userRole: string }> {
    return this.request<{ success: boolean; invitations: FamilyInvitation[]; userRole: string }>(
      `/invitations/list?familyId=${familyId}`
    );
  }
  
  /**
   * Anuluje zaproszenie do rodziny
   */
  async cancelInvitation(invitationId: string): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>('/invitations/cancel', {
      method: 'POST',
      body: JSON.stringify({ invitationId }),
    });
  }
  
  /**
   * Akceptuje zaproszenie do rodziny
   * @param request Dane zaproszenia
   * @param withAuth Czy żądanie ma zawierać token autoryzacji (domyślnie tak)
   * @returns Odpowiedź z API
   */
  async acceptInvitation(
    request: AcceptInvitationRequest, 
    withAuth: boolean = true
  ): Promise<AcceptInvitationResponse> {
    const options: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
      // Dodajemy cache: 'no-store' aby zapobiec cachowaniu odpowiedzi
      cache: 'no-store',
      // Dodajemy credentials: 'same-origin' aby wysyłać ciasteczka
      credentials: 'same-origin',
    };
    
    if (withAuth) {
      return this.request<AcceptInvitationResponse>('/invitations/accept', options);
    } else {
      // Wykonaj żądanie bez wymuszania autoryzacji, ale spróbuj dodać token jeśli dostępny
      const url = `${this.baseUrl}/invitations/accept`;
      logger.debug(`🌐 Public API Call: POST ${url}`);
      
      // Sprawdź czy mamy dostęp do Supabase session i dodaj token autoryzacji
      try {
        const { supabase } = await import('../lib/supabase');
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.access_token) {
          options.headers = {
            ...options.headers,
            'Authorization': `Bearer ${session.access_token}`
          };
          logger.debug('🔐 Dodano token autoryzacji do publicznego żądania');
        } else {
          logger.debug('🔓 Brak sesji użytkownika - żądanie anonimowe');
        }
      } catch (e) {
        logger.debug('🔓 Nie udało się pobrać sesji z Supabase:', e);
      }
      
      try {
        // Dodajemy unikalny parametr do URL, aby zapobiec cachowaniu
        const timestamp = new Date().getTime();
        const urlWithNocache = `${url}?_=${timestamp}`;
        
        const response = await fetch(urlWithNocache, options);
        
        if (!response.ok) {
          logger.debug(`❌ Error response status: ${response.status}`);
          logger.debug(`❌ Error response statusText: ${response.statusText}`);
          
          try {
            const errorData = await response.json();
            logger.debug(`❌ Error response data:`, errorData);
            throw new Error(errorData.error || errorData.details || `HTTP ${response.status}: ${response.statusText}`);
          } catch (jsonError) {
            throw new Error(`Błąd serwera (${response.status})`);
          }
        }
        
        const data = await response.json();
        logger.debug(`✅ Public API Success:`, data);
        return data;
      } catch (error) {
        logger.error(`❌ Public API Error for ${url}:`, error);
        throw error;
      }
    }
  }
  
  /**
   * Odrzuca zaproszenie do rodziny
   * @param token Token zaproszenia
   * @param withAuth Czy żądanie ma zawierać token autoryzacji (domyślnie tak)
   * @returns Odpowiedź z API
   */
  async rejectInvitation(
    token: string,
    withAuth: boolean = true
  ): Promise<{ success: boolean; message?: string }> {
    const options: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
      // Dodajemy cache: 'no-store' aby zapobiec cachowaniu odpowiedzi
      cache: 'no-store',
      // Dodajemy credentials: 'same-origin' aby wysyłać ciasteczka
      credentials: 'same-origin',
    };
    
    if (withAuth) {
      return this.request<{ success: boolean; message?: string }>('/invitations/reject', options);
    } else {
      // Wykonaj żądanie bez tokenu autoryzacji
      const url = `${this.baseUrl}/invitations/reject`;
      logger.debug(`🌐 Public API Call: POST ${url}`);
      
      try {
        // Dodajemy unikalny parametr do URL, aby zapobiec cachowaniu
        const timestamp = new Date().getTime();
        const urlWithNocache = `${url}?_=${timestamp}`;
        
        const response = await fetch(urlWithNocache, options);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Nieznany błąd serwera' }));
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        logger.debug(`✅ Public API Success:`, data);
        return data;
      } catch (error) {
        logger.error(`❌ Public API Error for ${url}:`, error);
        throw error;
      }
    }
  }

  /**
   * Pobiera listę członków rodziny
   */
  async getFamilyMembers(familyId: string): Promise<{ success: boolean; members: FamilyMember[] }> {
    return this.request<{ success: boolean; members: FamilyMember[] }>(
      `/family/members?familyId=${familyId}`
    );
  }

  /**
   * Dodaje członka do rodziny (używane wewnętrznie)
   */
  async addFamilyMember(familyId: string, userId: string, role: string): Promise<{ success: boolean; member: FamilyMember }> {
    return this.request<{ success: boolean; member: FamilyMember }>('/family/members', {
      method: 'POST',
      body: JSON.stringify({ familyId, userId, role }),
    });
  }

  async getFamilyPlan(
    familyId: string
  ): Promise<{ success: boolean; planId: 'basic' | 'pro' | 'max'; status: string; periodEnd?: string | null; priceId?: string | null }> {
    // 1) Cache z krótkim TTL (30s)
    const now = Date.now();
    const cached = this.familyPlanCache.get(familyId);
    if (cached && cached.expireAt > now) {
      return cached.data;
    }

    // 2) Deduplikacja równoległych żądań
    const inflight = this.inflightFamilyPlan.get(familyId);
    if (inflight) {
      return inflight;
    }

    const promise = this.request<{ success: boolean; planId: 'basic' | 'pro' | 'max'; status: string; periodEnd?: string | null; priceId?: string | null }>(
      `/family/plan?familyId=${familyId}`
    ).then((data) => {
      // Zapisz do cache i wyczyść inflight
      this.familyPlanCache.set(familyId, { data, expireAt: now + 30_000 });
      this.inflightFamilyPlan.delete(familyId);
      return data;
    }).catch((err) => {
      this.inflightFamilyPlan.delete(familyId);
      throw err;
    });

    this.inflightFamilyPlan.set(familyId, promise);
    return promise;
  }

  /**
   * Inicjuje checkout Stripe dla subskrypcji
   */
  async createCheckoutSession(params: { familyId: string; lookupKey: string; successUrl: string; cancelUrl: string }): Promise<{ url: string }> {
    return this.request<{ url: string }>(`/billing/checkout`, {
      method: 'POST',
      body: JSON.stringify(params)
    });
  }

  /**
   * Tworzy sesję Stripe Customer Portal do zarządzania subskrypcją
   */
  async createBillingPortalSession(params: { familyId: string; returnUrl: string }): Promise<{ url: string }> {
    return this.request<{ url: string }>(`/billing/portal`, {
      method: 'POST',
      body: JSON.stringify(params)
    });
  }

  /**
   * Usuwa konto użytkownika (GDPR Art. 17 — prawo do bycia zapomnianym)
   */
  async deleteAccount(): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('/account/delete', {
      method: 'DELETE',
    });
  }
}

// Singleton instance
export const apiService = new ApiService();

/**
 * Hook do używania API service w komponentach React
 */
export const useApiService = () => {
  return apiService;
};